import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';

import {
  AssetType,
  CompanyInfosFragment,
  FundraisingInfoFragment,
  IdentityType,
  IdentityVerificationStatus,
  SubscriptionInfosFragment,
  SubscriptionWorkflowType,
} from '@blockpulse3/graphql/hooks';
import { capitalizeFirstLetter } from '@blockpulse3/helpers';

import { AllowedHoldingMethodOption } from './AllowedHoldingMethodSelect';
import { ICrowdfundingSubscribers, IFundraisingSubscriber } from './FundraisingSubscribersModal';
import {
  ICrowdfundingFundraisingLegalSubscriberInformations,
  IFundraisingLegalSubscriberInformations,
} from './FundraisingSubscribersModal/Forms/LegalSubscriberInformationsForm/types';
import {
  ICrowdfundingFundraisingParametersForm,
  IPrivateFundraisingParametersForm,
  crowdfundingParametersDefaultValues,
  crowdfundingSubscriptionPeriodOptions,
  privateParametersDefaultValues,
  privateSubscriptionPeriodOptions,
} from './NewFundraising';

/**
 * privateFundraisingParametersDefaults.
 * Returns default private fundraising form values from a workspace.
 *
 * @param {CompanyInfosFragment} company
 * @returns {IPrivateFundraisingParametersForm}
 */
export function privateFundraisingParametersDefaults(
  company: CompanyInfosFragment,
): IPrivateFundraisingParametersForm {
  const date = dayjs();

  /* ** Default fundraising name ** */
  const name = `${company?.name} - ${capitalizeFirstLetter(date.format('MMMM'))} ${date.format(
    'YYYY',
  )}`;
  /* ** Default PricePerAction value ** */
  const pricePerShare = company?.nominalValue || 0;
  /* ** Default Valuation value ** */
  const valuation = company?.shareCapital || 0;

  const defaultFundrainsingParams = {
    ...privateParametersDefaultValues,
    name,
    pricePerShare,
    valuation,
  };

  return defaultFundrainsingParams;
}

/**
 * crowdfundingFundraisingParametersDefaults.
 * Returns default crowdfunding fundraising form values from a workspace.
 *
 * @param {CompanyInfosFragment} company
 * @returns {ICrowdfundingFundraisingParametersForm}
 */
export function crowdfundingFundraisingParametersDefaults(
  company: CompanyInfosFragment,
): ICrowdfundingFundraisingParametersForm {
  const privateDefaults = privateFundraisingParametersDefaults(company);

  return {
    ...privateDefaults,
    subscriptionPeriod: crowdfundingSubscriptionPeriodOptions[0],
    hardCap: null,
    softCap: null,
    subscriptionWorkflowType: SubscriptionWorkflowType.FIRST_IN,
    minimalAmount: null,
    maximalAmount: null,
    hasMifid: false,
  };
}

/**
 * getPrivateFundraisingParameters.
 * Format fundraising query into a private form object.
 *
 *
 * @param {FundraisingInfoFragment | undefined} fundraising
 * @returns {IFundraisingParametersForm}
 */
export function getPrivateFundraisingParameters(
  fundraising: FundraisingInfoFragment | undefined,
): IPrivateFundraisingParametersForm {
  if (!fundraising) {
    return privateParametersDefaultValues;
  }

  const holdingMethodsOption: AllowedHoldingMethodOption[] =
    fundraising?.allowedHoldingMethods?.map((holdingMethod) => {
      return {
        label: holdingMethod.toString(),
        value: holdingMethod,
      };
    }) || [];

  const selectedSubscriptionPeriod = privateSubscriptionPeriodOptions.find(
    (option) => option.value === fundraising?.subscriptionPeriod,
  );

  return {
    name: fundraising.name,
    assetType: AssetType.ORDINARY_SHARE,
    pricePerShare: fundraising.pricePerShare,
    subscriptionPeriod: selectedSubscriptionPeriod || privateSubscriptionPeriodOptions[0],
    valuation: fundraising.data.valuation || privateParametersDefaultValues.valuation,
    allowedHoldingMethods: holdingMethodsOption,
  };
}

/**
 * getCrowdfundingFundraisingParameters.
 * Format fundraising query into a crowdfunding form object.
 *
 * @param {FundraisingInfoFragment | undefined} fundraising
 * @returns {ICrowdfundingFundraisingParametersForm}
 */
export function getCrowdfundingFundraisingParameters(
  fundraising: FundraisingInfoFragment | undefined,
): ICrowdfundingFundraisingParametersForm {
  if (!fundraising) {
    return crowdfundingParametersDefaultValues;
  }

  const privateFundraisingParameters = getPrivateFundraisingParameters(fundraising);

  const selectedSubscriptionPeriod = crowdfundingSubscriptionPeriodOptions.find(
    (option) => option.value === fundraising.subscriptionPeriod,
  );

  let min = null;
  let max = null;

  const subscriptionDetailsStep = fundraising.subscriptionSteps?.find(
    (step) => step.__typename === 'SubscriptionDetailsStepType',
  );

  if (
    subscriptionDetailsStep &&
    subscriptionDetailsStep.__typename === 'SubscriptionDetailsStepType'
  ) {
    min = subscriptionDetailsStep.minimalAmount;
    max = subscriptionDetailsStep.maximalAmount;
  }

  const mifidStep = fundraising.subscriptionSteps?.find(
    (step) => step.__typename === 'MifidStepType',
  );
  const hasMifid = !!mifidStep;

  return {
    ...privateFundraisingParameters,
    subscriptionPeriod: selectedSubscriptionPeriod || crowdfundingSubscriptionPeriodOptions[0],
    hardCap: fundraising.hardCap ?? null,
    softCap: fundraising.softCap ?? null,
    subscriptionWorkflowType:
      fundraising.subscriptionWorkflowType ?? SubscriptionWorkflowType.FIRST_IN,
    minimalAmount: min ?? null,
    maximalAmount: max ?? null,
    hasMifid,
  };
}

/**
 * getPricePerShare.
 *
 * @param {BigNumber} valuation
 * @param {number} shareNumber
 * @returns {number}
 */
export function getPricePerShare(valuation: BigNumber, shareNumber: number): number {
  if (!valuation.isNaN()) {
    const shares = new BigNumber(shareNumber);
    return valuation.dividedBy(shares).dp(2).toNumber();
  }
  return 0;
}

/**
 * getValuation.
 *
 * @param {BigNumber} sharePrice
 * @param {number} shareNumber
 * @returns {number}
 */
export function getValuation(sharePrice: BigNumber, shareNumber: number): number {
  if (!sharePrice.isNaN()) {
    const shares = new BigNumber(shareNumber);
    return sharePrice.times(shares).dp(2).toNumber();
  }
  return 0;
}

/**
 * isLegalSubscriber.
 * Check if subscriber argument is a legal version or not.
 *
 * @param {IFundraisingSubscriber['subscriber'] | ICrowdfundingSubscriber['subscriber']} subscriber
 * @returns {subscriber is}
 */
export function isLegalSubscriber(
  subscriber: IFundraisingSubscriber['subscriber'] | ICrowdfundingSubscribers['subscriber'],
): subscriber is
  | IFundraisingLegalSubscriberInformations
  | ICrowdfundingFundraisingLegalSubscriberInformations {
  return (
    (
      subscriber as
        | IFundraisingLegalSubscriberInformations
        | ICrowdfundingFundraisingLegalSubscriberInformations
    ).name !== '' &&
    (
      subscriber as
        | IFundraisingLegalSubscriberInformations
        | ICrowdfundingFundraisingLegalSubscriberInformations
    ).name !== undefined
  );
}

/**
 * parseSubscriber.
 *
 * @param {SubscriptionInfosFragment} subscriber
 * @returns {IFundraisingSubscriber}
 */
export function parseSubscriber(subscriber: SubscriptionInfosFragment): IFundraisingSubscriber {
  if (subscriber.identity.type === IdentityType.COMPANY) {
    const companyRepIndividualIdentity =
      subscriber?.identity?.companyIdentity?.signer?.individualIdentity;
    return {
      isLegal: true,
      subscriber: {
        name: subscriber.identity.companyIdentity?.name || '',
        email: companyRepIndividualIdentity?.email || '',
        identificationNumber: subscriber.identity.companyIdentity?.registrationNumber || '',
        amount: subscriber.investAmount,
        verified: subscriber.identity.verificationStatus === IdentityVerificationStatus.APPROVED,
        admissionFees: subscriber.admissionFees,
        minimalAmount: subscriber.minimalAmount || null,
        maximalAmount: subscriber.maximalAmount || null,
      },
    };
  }
  return {
    isLegal: false,
    subscriber: {
      firstName: subscriber.identity.individualIdentity?.firstName || '',
      lastName: subscriber.identity.individualIdentity?.lastName || '',
      email: subscriber.identity.individualIdentity?.email || '',
      amount: subscriber.investAmount,
      verified: subscriber.identity.verificationStatus === IdentityVerificationStatus.APPROVED,
      name: '', // TypeScript type check, isLegalSusbcriber(). TODO: refactor
      admissionFees: subscriber.admissionFees,
      minimalAmount: subscriber.minimalAmount || null,
      maximalAmount: subscriber.maximalAmount || null,
    },
  };
}
